import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useState,
} from 'react';

import { Counter } from '../../counter';
import { Icon } from '../../icon';
import { Image } from '../../image';
import { cropText } from '../../lib/crop-text';
import { Typography } from '../../typography';
import { ItemsDisplayMode } from '../config';
import { useTabbarContext } from '../context';

import styles from './item.scss';

type TabBar2ItemProps = RefAttributes<HTMLButtonElement | HTMLLinkElement> &
  HTMLAttributes<HTMLButtonElement | HTMLLinkElement> & {
    icon?: {
      name?: string;
      activeName?: string;
      asImage?: boolean;
    };
    label?: {
      text?: string;
      [key: string]: unknown;
    };
    active?: boolean;
    counter?: {
      value?: number | string;
      className?: string;
      asNotificationBadge?: boolean;
      [key: string]: unknown;
    };
    href?: string;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-tabbar2-item';
const MAX_LABEL_LENGTH = 20;

const DEFAULT_COUNTER = {} as {
  value?: number | string;
  className?: string;
  asNotificationBadge?: boolean;
  [K: string]: any;
};
export const TabBar2Item: ForwardRefExoticComponent<TabBar2ItemProps> = forwardRef<
  HTMLButtonElement | HTMLLinkElement,
  TabBar2ItemProps
>(
  (
    {
      icon: { name: iconToken = '', activeName: activeIconToken = '', asImage = false } = {},
      label: { text: labelText = '', ...labelProps } = {},
      active,
      counter: {
        value: counterValue = null,
        className: counterClassName,
        asNotificationBadge: counterAsNotificationBadge,
        ...counterProps
      } = DEFAULT_COUNTER,
      dataTestId = COMPONENT_ID,
      style,
      className,
      href,
      ...rest
    },
    ref,
  ) => {
    const { itemsDisplayMode } = useTabbarContext();

    const withIcon =
      (iconToken || (active && activeIconToken)) &&
      [ItemsDisplayMode.Icons, ItemsDisplayMode.IconsTitles].includes(itemsDisplayMode);

    const withLabel = [ItemsDisplayMode.Titles, ItemsDisplayMode.IconsTitles].includes(
      itemsDisplayMode,
    );
    const withCounter = counterValue !== undefined && counterValue !== null;

    const [iconName, setIconName] = useState('');

    const counterElement = withCounter && (
      <span
        className={classNames(styles.counter, styles[itemsDisplayMode], counterClassName, {
          [styles['notification-badge']]: counterAsNotificationBadge,
        })}
        {...counterProps}>
        <Counter
          value={!counterAsNotificationBadge ? counterValue : 0}
          dataTestId={`${dataTestId}-counter`}
          className="caption-2-medium"
          notEmpty={!counterAsNotificationBadge}
        />
      </span>
    );

    useEffect(() => {
      if (!withIcon || asImage) return;

      const resolvedIconName = active
        ? activeIconToken || iconToken.replace('outlined', 'filled')
        : iconToken;
      setIconName(resolvedIconName);
    }, [active, withIcon, iconToken, activeIconToken, asImage]);

    const Tag = href ? 'a' : 'button';

    if (!withIcon && !withLabel) {
      return null;
    }

    return (
      <Tag
        className={classNames(className, styles['tabbar-item'], styles[itemsDisplayMode], {
          [styles.active]: active,
        })}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        style={style}
        {...(href && { href })}
        {...rest}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}>
        {withIcon && (
          <div
            className={styles['counter-wrapper']}
            style={{
              height: '24px',
            }}>
            {asImage ? (
              <Image
                dataTestId={`${dataTestId}-image`}
                width="24"
                height="24"
                name={iconToken}
                className={styles.icon}
              />
            ) : (
              <Icon
                dataTestId={`${dataTestId}-icon`}
                size={24}
                name={iconName}
                className={styles.icon}
              />
            )}

            {counterElement}
          </div>
        )}

        {withLabel && (
          <div className={styles['label-wrapper']}>
            <Typography
              dataTestId={`${dataTestId}-label`}
              variant="caption-2-medium"
              {...labelProps}>
              {cropText(labelText, MAX_LABEL_LENGTH)}
            </Typography>
          </div>
        )}

        {itemsDisplayMode === ItemsDisplayMode.Titles && withLabel && counterElement}
      </Tag>
    );
  },
);

TabBar2Item.displayName = 'TabBar2Item';
